import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Users, Monitor, Shield, ArrowLeft, Box,
    Building,
    Activity,
    Network,
    HardDrive,
    ScrollText,
} from 'lucide-react';
import AgentsTable from './Agent/AgentTable';
import Cameras from './Cameras';
import baseapi from '../services/baseapi';
import { hasPermission } from '../utils/permissionsHelper';
import AssetsTable from './Assets/AssetsTable';
import EventsTable from './Events/EventsTable';
import TopologyBuilder from './AssetTopology/TopologyBuilder';
import NVRDashboard from './NVR/NVRDashboard';
import NVRLogsTable from './NVR/NVRLogsTable';

const ClientDetails: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const clientId = searchParams.get('client_id');
    const activeTab = searchParams.get('tab') || 'assets';
    const path = searchParams.get('path') || 'clients';

    const [clientName, setClientName] = useState<string>('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchClientDetails = async () => {
            if (!clientId) return;
            try {
                const response = await baseapi.get(`/clients/${clientId}`);
                if (response.data.status) {
                    setClientName(response.data.data.name);
                }
            } catch (error) {
                console.error('Error fetching client details:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchClientDetails();
    }, [clientId]);

    const handleTabChange = (tab: string) => {
        setSearchParams({ client_id: clientId || '', tab, path });
    };

    // Define all available tabs for this view
    const AVAILABLE_TABS = [
        { id: "assets", label: "Assets", icon: Box, permissionKey: "assets", color: "text-gray-400" },
        { id: "agents", label: "Agents", icon: Users, permissionKey: "agents" },
        { id: "monitoring", label: "Monitoring System", icon: Monitor, permissionKey: "camera" },
        { id: "events", label: "Events", icon: Activity, permissionKey: "events", color: "text-gray-400" },
        { id: "topology", label: "Topology", icon: Network, permissionKey: "topology", color: "text-gray-400" },
        { id: "nvr", label: "NVR", icon: HardDrive, permissionKey: "assets" },
        { id: "nvr_logs", label: "NVR Logs", icon: ScrollText, permissionKey: "assets" },
    ];

    // Filter tabs based on user permissions
    const visibleTabs = AVAILABLE_TABS.filter(tab => {
        if (!tab.permissionKey) return true; // Show if no specific permission required
        return hasPermission(tab.permissionKey, 'read');
    });

    // Auto-redirect if the current tab is not permitted but others are
    useEffect(() => {
        const isCurrentTabVisible = visibleTabs.some(tab => tab.id === activeTab);

        if (!isCurrentTabVisible && visibleTabs.length > 0) {
            setSearchParams({ client_id: clientId || '', tab: visibleTabs[0].id, path });
        }
    }, [activeTab, visibleTabs, clientId, path, setSearchParams]);

    if (!clientId) {
        return (
            <div className="p-8 text-center bg-slate-50 min-h-screen flex flex-col items-center justify-center">
                <p className="text-slate-500 font-bold mb-4">No client selected.</p>
                <button
                    onClick={() => navigate(`/${path}`)}
                    className="flex items-center justify-center gap-2 text-[#422568] font-bold hover:underline"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to {path.charAt(0).toUpperCase() + path.slice(1)}
                </button>
            </div>
        );
    }

    // Function to render the correct content based on the active tab
    const renderTabContent = () => {
        if (visibleTabs.length === 0) {
            return (
                <div className="p-20 text-center flex flex-col items-center justify-center h-full">
                    <Shield className="w-12 h-12 text-slate-300 mb-4" />
                    <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">
                        No permissions to view this client's resources.
                    </p>
                </div>
            );
        }

        switch (activeTab) {
            case 'agents':
                return <AgentsTable clientId={Number(clientId)} />;
            case 'monitoring':
                return <Cameras clientId={Number(clientId)} />;
            case 'assets':
                return <AssetsTable clientId={Number(clientId)} />;
            case 'events':
                return <EventsTable clientId={Number(clientId)} />;
            case 'topology':
                return <TopologyBuilder clientId={Number(clientId)} />;
            case 'nvr':
                return <NVRDashboard clientId={Number(clientId)} />;
            case 'nvr_logs':
                return <NVRLogsTable clientId={Number(clientId)} />;
            default:
                return null;
        }
    };

    return (
        <div className="bg-white h-screen flex flex-col overflow-hidden">
            {/* Header - Fixed to top */}
            <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
                <div className="px-4 pt-3 pb-1">
                    <div className="flex items-center gap-4">
                        {/* Dynamic back navigation based on 'path' parameter */}
                        <button
                            onClick={() => navigate(`/${path}`)}
                            className="p-2 bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:text-slate-800 rounded-lg transition-colors text-slate-500 shadow-sm"
                            title="Go Back"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                        <div className="flex items-center gap-3">
                            <div className="bg-[#422568] p-2 rounded-lg shadow-sm">
                                <Building className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-extrabold text-slate-800 leading-none">
                                    {loading ? 'Loading...' : clientName}
                                </h1>
                                <p className="text-xs font-semibold text-slate-500 mt-1">Site Overview</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs - Scrollable horizontally if there are many */}
                <div className="px-4 flex items-center gap-1 overflow-x-auto custom-scroll">
                    {visibleTabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => handleTabChange(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-t-xl transition-all border-b-[3px] whitespace-nowrap ${activeTab === tab.id
                                ? 'text-white border-[#422568] bg-[#422568]/80'
                                : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-50'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 bg-white flex flex-col overflow-y-auto custom-scroll relative">
                {renderTabContent()}
            </div>
        </div>
    );
};

export default ClientDetails;